import React from 'react'
import { Card, Select, Button, Space, Switch, Tooltip } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { ExprNode, GroupNode } from '../types'
import { ExpressionEditorConfig } from '../fields'
import { createCondition, createGroup, createRaw } from '../expression'
import ExpressionCondition from './ExpressionCondition'
import ExpressionRaw from './ExpressionRaw'

const { Option } = Select

type Props = {
  node: GroupNode
  config: ExpressionEditorConfig
  onChange: (node: GroupNode) => void
  onRemove?: () => void
  isRoot?: boolean
}

export default function ExpressionGroup({ node, config, onChange, onRemove, isRoot }: Props) {
  const replaceChild = (index: number, child: ExprNode) => {
    const children = node.children.slice()
    children[index] = child
    onChange({ ...node, children })
  }

  const removeChild = (index: number) => {
    const children = node.children.slice()
    children.splice(index, 1)
    onChange({ ...node, children })
  }

  const addChild = (child: ExprNode) => {
    onChange({ ...node, children: node.children.concat(child) })
  }

  const title = (
    <Space>
      <Tooltip title='Invert this group (NOT)'>
        <Switch
          size='small'
          checkedChildren='NOT'
          unCheckedChildren='NOT'
          checked={!!node.negate}
          onChange={negate => onChange({ ...node, negate })}
        />
      </Tooltip>
      <span>Match</span>
      <Select
        size='small'
        style={{ width: 80 }}
        value={node.operator}
        onChange={(operator: 'AND' | 'OR') => onChange({ ...node, operator })}
      >
        <Option value='AND'>AND</Option>
        <Option value='OR'>OR</Option>
      </Select>
      <span>of:</span>
    </Space>
  )

  const extra = onRemove ? (
    <Button type='text' danger icon={<DeleteOutlined />} onClick={onRemove} title='Remove group' />
  ) : null

  return (
    <Card
      size='small'
      title={title}
      extra={extra}
      type={isRoot ? undefined : 'inner'}
      style={{
        marginBottom: 8,
        background: isRoot ? undefined : '#fafafa',
        borderColor: node.negate ? '#ff7875' : undefined
      }}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='small'>
        {node.children.length === 0 && (
          <span style={{ color: '#999' }}>No conditions yet — add one below.</span>
        )}

        {node.children.map((child, index) => {
          if (child.kind === 'group') {
            return (
              <ExpressionGroup
                key={child.id}
                node={child}
                config={config}
                onChange={updated => replaceChild(index, updated)}
                onRemove={() => removeChild(index)}
              />
            )
          }
          if (child.kind === 'raw') {
            return (
              <ExpressionRaw
                key={child.id}
                node={child}
                onChange={updated => replaceChild(index, updated)}
                onRemove={() => removeChild(index)}
              />
            )
          }
          return (
            <ExpressionCondition
              key={child.id}
              node={child}
              config={config}
              onChange={updated => replaceChild(index, updated)}
              onRemove={() => removeChild(index)}
            />
          )
        })}

        <Space>
          <Button
            size='small'
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => addChild(createCondition(config))}
          >
            Add condition
          </Button>
          <Button
            size='small'
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => addChild(createGroup(config))}
          >
            Add group
          </Button>
          <Button
            size='small'
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => addChild(createRaw())}
          >
            Add expression
          </Button>
        </Space>
      </Space>
    </Card>
  )
}
